/**
 * **传输层的接口。通货是 peer id（字符串），不是 peer 对象。**
 *
 * 这一条是整个 `net/` 里最要紧的设计约束，而且是**从 `old_repo` 的失败里直接
 * 学到的一课**：它的 `TinySimplePeer` 对象穿过了每一个 handler 签名
 * （`onRequest((req, peer) => …)`、`request(peer, …)`、`ping(peer)`），
 * 于是换传输库就等于改每一处调用点。全文只有 28/755 行提到那个类型，
 * 但它是**贯穿性**的 —— 宽，不深
 * （`docs/archive/netcode-s3-evaluation.md` §2.1）。
 *
 * 所以这里立一条规矩：
 *
 * > **上层永远只拿到 `string` 形式的 peer id。**
 * > `import 'trystero'` 只允许出现在 `net/trystero.ts` 一个文件里。
 *
 * 好处不止"将来好换库"：因为接口小，测试里塞一个内存假实现就能把
 * `peer.ts` / `session.ts` 完整跑起来（`test/net/harness.ts`），
 * 还能顺手注入延迟、丢包、断线。
 *
 * ## 两个通道
 *
 * | 通道 | 语义 | 底层 |
 * | --- | --- | --- |
 * | `send` / `onMessage` | 发了就不管（通知） | Trystero 的 message action |
 * | `request` / `onRequest` | 等一个响应（RPC） | Trystero 的 request action |
 *
 * 两条通道共用**同一条 DataChannel**，所以它们之间的**相对顺序是有保证的**。
 * `session.ts` 的"先把终局视图推完、再宣告收摊"就靠这一点
 * （`docs/netcode-s3.md` §6.1）。
 */

export type Unsubscribe = () => void;

/**
 * 传输失败的四种结局。
 *
 * 和 Trystero 的 `error.kind` 一一对应 —— 但**上层不该知道 Trystero 有 `kind`**，
 * 所以在这一层翻译掉：
 *
 * | Trystero | 这里 |
 * | --- | --- |
 * | `timeout` | `timeout` |
 * | `disconnected` | `disconnected` |
 * | `aborted` | `aborted` |
 * | `rejected` | `rejected` |
 */
export type TransportErrorKind = 'timeout' | 'disconnected' | 'aborted' | 'rejected';

export class TransportError extends Error {
  readonly kind: TransportErrorKind;

  constructor(kind: TransportErrorKind, message: string) {
    super(message);
    this.name = 'TransportError';
    this.kind = kind;
  }
}

export interface Transport {
  /** 我自己的 peer id。由传输层分配（Trystero 的 `selfId`） */
  readonly selfId: string;

  /** 当前在线的 peer id 列表。**不含自己** */
  peers(): readonly string[];

  /**
   * 发一条通知。`to` 为 `null` 表示广播。
   *
   * 刻意**不返回 Promise**：通知是"发了就不管"的。真实实现里的异步失败
   * （比如 peer 刚断）通过 `onError` 上报，而不是让每个调用点都去 catch
   * 一个它无能为力的异常。
   */
  send(to: string | null, raw: string): void;

  /**
   * 发一个请求，等一条响应。
   *
   * ⚠️ **超时是传输层的责任**，不是调用方的：只有传输层知道
   * "这条信道多久没动静算死"。失败时抛 `TransportError`。
   */
  request(to: string, raw: string, timeoutMs: number): Promise<string>;

  /** 收到通知 */
  onMessage(handler: (from: string, raw: string) => void): Unsubscribe;

  /**
   * 收到请求。**返回值就是要发回去的响应文本**。
   *
   * 之所以让传输层拿到"响应文本"而不是"结果对象"：这样
   * "把结果序列化成信封"这件事只发生在一个地方（`rpc.ts` 注册的那个 handler），
   * 而换传输时不必重写它。
   */
  onRequest(handler: (from: string, raw: string) => string | Promise<string>): Unsubscribe;

  onPeerJoin(handler: (id: string) => void): Unsubscribe;
  onPeerLeave(handler: (id: string) => void): Unsubscribe;
  /** 传输层自身的异常（发失败、信令断了……）。仅供观测，不改变逻辑 */
  onError(handler: (error: unknown) => void): Unsubscribe;

  /**
   * 往返延迟，毫秒。
   *
   * ⚠️ Trystero 的 `ping()` **没有超时** —— 对端不回 pong 就是一个永不落地的
   * Promise。所以接口上强制带 `timeoutMs`，由实现去 race。
   */
  ping(id: string, timeoutMs: number): Promise<number>;

  /** 离开房间。之后这个对象不可再用（`S3Peer` 允许**新开**一个，但不复用旧的） */
  destroy(): void;
}
